import { useEffect, useRef, useState } from 'react';

import { useProjectStore } from '../data/StoreContext.js';
import { isDevEnv } from '../environment.js';
import { useShortcuts, modKey } from '../hooks/useShortcuts.js';
import { useAppState, useStore } from '../hooks/useStore.js';
import { APPLY_BLOCKED_TOAST } from '../messages.js';

import { BliksysMark } from './BliksysMark.js';
import { Editor, computeBackTarget, visiblePointer } from './Editor.js';
import { Icon } from './Icon.js';
import { toast } from './Toast.js';

import './AppShell.css';

interface Props {
  projectName: string;
  onSave: () => void;
  /** Send current state to the configured deploy target. Optional —
   * the demo project doesn't have one. */
  onDeploy?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  onSettings?: (() => void) | undefined;
  /** Show "Edit schema" button. Hidden in schema-editor mode. */
  onSwitchToSchema?: (() => void) | undefined;
  /** Show "Back to content" button. Hidden in content-editor mode. */
  onSwitchToContent?: (() => void) | undefined;
  /**
   * File handlers — import replaces the editor's data with an uploaded
   * file's contents (with migration if appropriate); export downloads
   * the current state as JSON. Each editor wires these to its own
   * payload (schema vs content).
   */
  onImport?: ((file: File) => void) | undefined;
  onExport?: (() => void) | undefined;
  /** Label for the import button — "Import schema" / "Import content". */
  importLabel?: string;
  /** Label for the export button. */
  exportLabel?: string;
  /**
   * Visual tone — distinguishes the schema editor from the content
   * editor at a glance via accent color and a small tag in the
   * top bar. Without this, the two surfaces look identical.
   */
  tone?: 'content' | 'schema';
  /**
   * Optional banner rendered above the editor body. Used by the schema
   * editor to surface consistency warnings; the AppShell stays neutral
   * about content.
   */
  topBanner?: React.ReactNode;
  /**
   * Slot rendered in the top-right of the header. Cloud mode drops in
   * the UserMenu here; free mode leaves it undefined.
   */
  headerExtra?: React.ReactNode;
}

export function AppShell({
  projectName,
  onSave,
  onDeploy,
  onClose,
  onSettings,
  onSwitchToSchema,
  onSwitchToContent,
  onImport,
  onExport,
  importLabel,
  exportLabel,
  tone,
  topBanner,
  headerExtra,
}: Props): JSX.Element {
  const store = useStore();
  const state = useAppState();
  const storeMode = useProjectStore().mode;
  const importInputRef = useRef<HTMLInputElement>(null);
  const overflowRef = useRef<HTMLDetailsElement>(null);
  // Tracks whether the narrow-viewport overflow menu is open, mirrored to
  // the <details> element. We need this in state for the popover items'
  // onClick wrappers to close the menu after activating an action.
  const [, setOverflowOpen] = useState(false);

  // Close the narrow-viewport overflow menu on outside click and Escape.
  // Same pattern as ProjectRowMenu; native <details> doesn't dismiss
  // either of these on its own.
  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      const d = overflowRef.current;
      if (!d?.open) return;
      if (e.target instanceof Node && !d.contains(e.target)) {
        d.open = false;
        setOverflowOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && overflowRef.current?.open) {
        overflowRef.current.open = false;
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const closeOverflow = (): void => {
    if (overflowRef.current) {
      overflowRef.current.open = false;
      setOverflowOpen(false);
    }
  };

  useShortcuts([
    {
      combo: 'esc',
      allowInInputs: true,
      handler: () => {
        if (state.pointer === '') return;
        (document.activeElement as HTMLElement | null)?.blur?.();
        // Esc inside a draft is the same as the editor's "Apply" button:
        // commit the in-flight item and zoom out to the draft's parent.
        // For pointers BELOW the draft, normal Esc-zoom still applies
        // (we're descending out of a leaf within the draft). If the
        // draft has validation issues, applyDraft refuses (returns false)
        // and we stay put — the user can fix the issue or use the
        // explicit Discard button.
        if (state.draftPointer !== null && state.pointer === state.draftPointer) {
          const ok = store.applyDraft();
          if (!ok) {
            toast(APPLY_BLOCKED_TOAST, 'error');
            return;
          }
          // Same fix-up as the editor's handleApply — if the store
          // landed us on a hidden inline-expanded ancestor, hop further.
          const after = store.getState();
          const visible = visiblePointer(after.pointer, after.schema, after.data);
          if (visible !== after.pointer) store.navigate(visible);
          return;
        }
        // Use the same "skip-to-meaningful-ancestor" logic the back
        // button uses, so Esc never lands the user on an inline-expanded
        // intermediate (e.g. /pages — hidden in the breadcrumb because
        // its children render in their parent's view). Without this,
        // pressing Esc inside an item would surface a view that's
        // unreachable from anywhere else in the UI.
        const target = computeBackTarget(state.pointer, state.schema, state.data);
        if (target === null) return;
        if (state.draftPointer !== null && store.navigationLeavesDraft(target.pointer)) {
          // Don't auto-prompt on Esc — Esc is meant to be cheap and
          // reversible. Just don't navigate; the user can use the
          // breadcrumb or back button to get explicit Apply/Discard
          // dialogs.
          return;
        }
        store.navigate(target.pointer);
      },
    },
    {
      combo: 'mod+s',
      allowInInputs: true,
      handler: () => {
        if (state.dirty) onSave();
      },
    },
    {
      combo: 'mod+/',
      allowInInputs: true,
      handler: () => {
        // Whichever mode-switch button is mounted is the legal action;
        // the other will be undefined.
        if (onSwitchToSchema) onSwitchToSchema();
        else if (onSwitchToContent) onSwitchToContent();
      },
    },
    {
      combo: 'mod+,',
      allowInInputs: true,
      handler: () => {
        if (onSettings) onSettings();
      },
    },
  ]);

  const errorCount = state.issues.length;

  // Secondary action cluster. Items render inline at ≥768px viewports
  // and inside a single overflow menu below that — see `.app-bar-actions`
  // / `.app-bar-overflow` in AppShell.css. Listing them here once keeps
  // the two render passes in lockstep.
  interface ActionItem {
    key: string;
    label: string;
    /** Optional richer title (e.g. with shortcut hint). Falls back to label. */
    title?: string;
    onClick: () => void;
    icon: JSX.Element;
  }
  const actionItems: ActionItem[] = [];
  if (onImport) {
    actionItems.push({
      key: 'import',
      label: importLabel ?? 'Import',
      onClick: () => importInputRef.current?.click(),
      icon: <Icon name="import" size={14} />,
    });
  }
  if (onExport) {
    actionItems.push({
      key: 'export',
      label: exportLabel ?? 'Export',
      onClick: onExport,
      icon: <Icon name="export" size={14} />,
    });
  }
  if (onDeploy) {
    actionItems.push({
      key: 'deploy',
      label: 'Deploy',
      onClick: onDeploy,
      icon: <Icon name="arrow-right" size={14} />,
    });
  }
  if (onSettings) {
    actionItems.push({
      key: 'settings',
      label: 'Settings',
      title: `Settings (${modKey()}+,)`,
      onClick: onSettings,
      icon: <Icon name="gear" size={14} />,
    });
  }

  return (
    <div className={`app-shell${tone ? ` app-shell-tone-${tone}` : ''}`}>
      <a href="#app-main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="app-bar">
        <div className="app-bar-left">
          {onClose ? (
            <button
              type="button"
              className="app-logo app-logo-button"
              onClick={onClose}
              aria-label="Back to projects"
              title="Back to projects"
            >
              <BliksysMark size={20} />
            </button>
          ) : (
            <div className="app-logo" aria-hidden="true">
              <BliksysMark size={20} />
            </div>
          )}
          {isDevEnv && (
            <span className="app-env-badge" title="Local development build">
              Dev
            </span>
          )}
          <span
            className={`app-store-badge app-store-badge-${storeMode}`}
            title={
              storeMode === 'cloud'
                ? 'Cloud project — saved to your organization'
                : 'Local project — saved only in this browser'
            }
          >
            {storeMode === 'cloud' ? 'Cloud' : 'Local'}
          </span>
          <div className="app-project">
            <div className="app-project-name">
              {projectName}
              {tone && (
                <span className={`app-mode-tag app-mode-tag-${tone}`}>
                  {tone === 'schema' ? 'Schema' : 'Content'}
                </span>
              )}
            </div>
            <div className="app-project-status">
              {state.dirty ? 'Unsaved changes' : 'All changes saved'}
            </div>
          </div>
        </div>

        <div className="app-bar-right">
          {headerExtra}
          <span
            className="app-issue-badge-region"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {errorCount > 0 && (
              <span className="app-issue-badge">
                <Icon name="alert-circle" size={12} strokeWidth={2.4} />
                {errorCount} issue{errorCount === 1 ? '' : 's'}
              </span>
            )}
          </span>
          {onSwitchToSchema && (
            <button
              type="button"
              className="app-mode-button"
              onClick={onSwitchToSchema}
              title={`Edit the project's schema (${modKey()}+/)`}
              aria-keyshortcuts={`${modKey()}+/`}
            >
              <Icon name="chevrons-right" size={14} />
              <span className="app-button-label">Edit schema</span>
            </button>
          )}
          {onSwitchToContent && (
            <button
              type="button"
              className="app-mode-button"
              onClick={onSwitchToContent}
              title={`Back to editing content (${modKey()}+/)`}
              aria-keyshortcuts={`${modKey()}+/`}
            >
              <Icon name="file-document" size={14} />
              <span className="app-button-label">Edit content</span>
            </button>
          )}
          {onImport && (
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onImport(f);
                // Reset so re-uploading the same filename triggers a change.
                e.target.value = '';
              }}
            />
          )}
          {/* Inline action cluster — visible at ≥768px viewports. */}
          <div className="app-bar-actions">
            {actionItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className="app-mode-button"
                onClick={item.onClick}
                title={item.title ?? item.label}
                aria-label={item.label}
                {...(item.key === 'settings' ? { 'aria-keyshortcuts': `${modKey()}+,` } : {})}
              >
                {item.icon}
                <span className="app-button-label">{item.label}</span>
              </button>
            ))}
          </div>
          {/* Overflow menu — visible at <768px viewports. Same items as
              above; CSS toggles which container is shown. */}
          {actionItems.length > 0 && (
            <details
              ref={overflowRef}
              className="app-bar-overflow"
              onToggle={(e) => setOverflowOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary
                className="app-bar-overflow-trigger"
                aria-label="More actions"
                title="More actions"
              >
                <Icon name="more-horizontal" size={16} />
              </summary>
              <div className="app-bar-overflow-popover" role="menu">
                {actionItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="app-bar-overflow-item"
                    role="menuitem"
                    onClick={() => {
                      item.onClick();
                      closeOverflow();
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </details>
          )}
          <button
            type="button"
            className={
              'app-save-button' +
              (state.dirty ? ' app-save-button-primary' : ' app-save-button-disabled')
            }
            onClick={onSave}
            disabled={!state.dirty}
            aria-keyshortcuts={`${modKey()}+S`}
          >
            Save
            <kbd>{modKey()} S</kbd>
          </button>
        </div>
      </header>

      {topBanner}
      <main className="app-main" id="app-main-content" tabIndex={-1}>
        <Editor />
      </main>
    </div>
  );
}
